// FLAG:useDocuAi — child-faced search tile: label + parent hint for a list row whose
// full-text match came from a DMS child document (attachment / report / validation report /
// extracted XML). `matchedChild` is attached server-side ONLY for useDocuAi tenants
// (getDocumentListSSR), so flag-OFF rows never reach these components.
import React from 'react'
import Tooltip from '@mui/material/Tooltip'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import ThemeInterface from '@/theme-interface'
import customDecodeUri from '@/utils/customDecodeUri'

export interface MatchedChild {
  childVersionId?: string
  childMinioId?: string
  childName?: string
  relationKind?: string
  thumbnailUrl?: string
  score?: number
}

// Same wording as the drawer preview switcher (invoice-preview-documents.ts), so the
// tile and the dropdown the click leads to speak the same language.
export const getMatchedChildLabel = (matchedChild: MatchedChild): string => {
  const name = customDecodeUri(matchedChild.childName || '')
  switch (matchedChild.relationKind) {
    case 'validation-report':
      return lang.documents.previewCarouselWrapperTabs.validationReport
    case 'business-terms-report':
      return lang.documents.previewCarouselWrapperTabs.report
    case 'extracted-xml':
      return lang.documents.previewCarouselWrapperTabs.xml
    case 'embedded-attachment':
      return `Anlage: ${name}`
    default:
      return name
  }
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    margin: '0 6px',
    minWidth: 0,
  },
  childLabel: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    cursor: 'default',
  },
  parentHint: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    cursor: 'default',
    fontSize: theme.fontSizes.tiny,
    opacity: 0.65,
    color: theme.text,
  },
}))

interface MatchedChildFilenameProps {
  matchedChild: MatchedChild
  parentName?: string
}

// Two-line tile caption: the matched child's label on top, the parent document it
// belongs to as a muted hint below. Row identity stays the parent — display only.
const MatchedChildFilename = ({ matchedChild, parentName }: MatchedChildFilenameProps) => {
  const classes = useStyles()
  const label = getMatchedChildLabel(matchedChild)
  const decodedParentName = customDecodeUri(parentName || '')
  return (
    <span className={classes.wrapper}>
      <Tooltip title={label}>
        <span className={classes.childLabel}>{label}</span>
      </Tooltip>
      <Tooltip title={decodedParentName}>
        <span className={classes.parentHint}>{lang.documents.childOfParent(decodedParentName)}</span>
      </Tooltip>
    </span>
  )
}

export default MatchedChildFilename
